import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import NTMSelect from "../../../components/NTMSelect";
import { useTranslation } from "react-i18next";
import { languages } from "../../../constants/lenguages";
import { currency } from "../../../constants/currency";
import { useGeneralSettingsManagerFields } from "./settingsFields";
import RenderFields from "./RenderFields";
import { useBikeType } from "constants/bikeType";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import CreateIcon from "@mui/icons-material/Create";
import CustomAwardModal from "components/modals/CustomAwardModal";
import StartIconButton from "components/buttons/StartIconButton";
import { post, getErrorMessage, DASH_AWARDS } from "services/Client";
import { useGetOrganizations } from "services/ContentManager";

/**
 * Mappa i fields in un oggetto con 
 * field.name come chiave
 * e come valore un oggetto contenente le varie regole di validazione
 * se il campo e required, minimo se è number etc.
 * @param {*} fields 
 * @returns 
 */
function validationRulesByFieldName(fields) {
    return Object.fromEntries(
        fields.map(field => [
            field.name,
            {
                required: field.required,
                min: field.min
                // ...aggiungere altre regole di validazione per il campo
            }
        ])
    );
};

/**
 * Rimuove eventuali errori per un campo
 */
function clearFieldError(errors, setError, fieldName) {
    if (errors[fieldName]) {
        const updatedErrors = { ...errors };
        delete updatedErrors[fieldName];
        setError(updatedErrors);
    }
}

/**
 * Aggiorna lo stato onError in base alla presenza di errori
 */
function updateOnErrorStatus(errors, onError) {
    if(Object.keys(errors).length === 0)
        onError(false);
}

/**
 * Valida campo richiesto
 * ritorna la chiave di errore
 */
function validateRequired(required, value) {
    if (required && String(value).trim() === '') {
        return 'mandatoryField';
    }
    return null;
}

/**
 * Valida valore minimo
 * ritorna la chiave di errore
 */
function validateMin(min, value) {
    if (min != null && Number(value) < min) {
        return 'lessThanMinValueAlert';
    }
    return null;
}

/**
 * Valida un campo secondo le sue regole
 */
function validateFieldValue(rules, value) {
    return (
        validateRequired(rules.required, value) ||
        validateMin(rules.min, value)
        // puoi aggiungere qui altre regole
    );
}

export default function GeneralSettingsManager({ values, setValues, onError }) {

    const { t } = useTranslation()
    let [error, setError] = useState({});
    const fields = useGeneralSettingsManagerFields(values, setValues);
    const validationRules = validationRulesByFieldName(fields);
    const bikeTypeLabels = useBikeType();

    const { id } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { organizations = [] } = useGetOrganizations();
    let [inviteModal, setInviteModal] = useState(null); // null | 'inviter' | 'invited'
    const currentOrg = { id: Number(id), title: organizations.find(o => String(o.id) === String(id))?.title || "" };

    // Crea un custom award flaggato come premio "invita un amico" per questa iniziativa: il flag
    // (inviterReward/invitedReward) fa sì che il backend lo associi al ruolo (uno per ruolo).
    const saveInviteAward = (formValues) => {
        enqueueSnackbar(t('saving...'), { variant: "info" });
        post(`${DASH_AWARDS}/customs`, { body: formValues })
            .then(() => enqueueSnackbar(t('saved'), { variant: "success" }))
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => queryClient.invalidateQueries("AwardsCustom"));
    };

    const handleFieldChange = (fieldName) => (newValue) => {
        const updatedValues = { ...values, [fieldName]: newValue };
        setValues(updatedValues);
        validateField(fieldName, newValue);
    };

    const validateField = (fieldName, newValue) => {

        clearFieldError(error, setError, fieldName);
        updateOnErrorStatus(error, onError);

        const rules = validationRules[fieldName];
        if (!rules) return;

        const errorMessage = validateFieldValue(rules, newValue);
        if (errorMessage) {
            onError(true);
            setError(errors => ({ ...errors, [fieldName]: t(errorMessage) }));
        }
    };
    

    return <Grid container spacing={4} style={{ margin: 0, width: "100%", marginBottom: "0.5rem" }}>

        <Grid item xs={12} md={6} style={{ padding: "16px" }}>
            <NTMSelect
                color={'primary'}
                label={t('locale')}
                items={languages.map(l => ({ value: l.alternativeCode, text: l.label }))}
                onChange={value => {
                    let newValues = {
                        ...values,
                        locale: value
                    }
                    setValues(newValues);
                }}
                value={values.locale}
            />
        </Grid>
        <Grid item xs={12} md={6} style={{ padding: "16px" }}>
            <NTMSelect
                color={'primary'}
                label={t('currencyLabel')}
                items={currency.map(l => ({ value: l.alternativeCode, text: l.label }))}
                onChange={value => {
                    let newValues = {
                        ...values,
                        currency: value
                    }
                    setValues(newValues);
                }}
                value={values.currency}
            />
        </Grid>

        <Grid item xs={12} md={6} style={{ padding: "16px", display: "flex", alignItems: "center" }}>
            <StartIconButton
                startIcon={<CreateIcon/>}
                title={t('addInviterReward', 'Premio per chi invita')}
                onClick={() => setInviteModal('inviter')}
            />
        </Grid>
        <Grid item xs={12} md={6} style={{ padding: "16px", display: "flex", alignItems: "center" }}>
            <StartIconButton
                startIcon={<CreateIcon/>}
                title={t('addInvitedReward', 'Premio per chi è invitato')}
                onClick={() => setInviteModal('invited')}
            />
        </Grid>

        <Grid item xs={12} md={6} style={{ padding: "16px" }}>
            <NTMSelect
                color={'primary'}
                label={t('forcedBikeType')}
                items={[
                    { id: "", name: t('none') },
                    ...bikeTypeLabels
                ].map(l => ({ value: String(l.id), text: l.name }))}
                onChange={value => {
                    let newValues = {
                        ...values,
                        forcedBikeType: value === "" ? null : value
                    }
                    setValues(newValues);
                }}
                value={values.forcedBikeType ?? ""}
            />
        </Grid>

        <RenderFields fields={fields} values={values} error={error} onTextChange={handleFieldChange}/>

        <CustomAwardModal
            open={!!inviteModal}
            onClose={() => setInviteModal(null)}
            onSubmit={saveInviteAward}
            defaultValues={inviteModal === 'inviter' ? { inviterReward: true } : { invitedReward: true }}
            organizations={[currentOrg]}
        />

    </Grid>;
}
